import React, { useState, useEffect, useMemo } from "react";
import { useTimer } from "react-timer-hook";
import { useForm } from "react-hook-form";
import { useValidatePhoneMutation, useStudentLoginOTPMutation } from "../../store/auth/auth.api";
import { useSendOTPWithRecaptcha } from "../../store/auth/otp.api";
import { PhoneInput } from "./form/PhoneInput";
import { loginOTPFormResolver } from "../../constants/resolvers";
import { ROUTES } from "../../constants/routes";
import OtpInput from "react-otp-input";
import loaderGif from "../../assets/gifs/LoaderGif.gif";
import { useHistory } from "react-router-dom/";
import { useWebEngageMutation } from "../../store/ApiMutaion";
import useLeadCapture from "../UseLeadCapture";
import { useRecaptchaDismissalDetector } from "../../hooks/useRecaptchaDismissalDetector";
import { DEFAULT_COUNTRY } from "../../constants/country-config";
import { useAnalytics } from "../../contexts/AnalyticsContext";
import { webengageLogin } from "../../webengage/webengageService";

export const LoginOtp = ({ toggleTAndC, togglePAndP }) => {
  const [onStudentLoginOTP] = useStudentLoginOTPMutation();
  const [sendOTP, { isLoading: sendOTPLoading, isUninitialized }] = useSendOTPWithRecaptcha();
  const [verifyPhoneNumber] = useValidatePhoneMutation();
  const form = useForm({ resolver: loginOTPFormResolver });
  const { handleSubmit, setError, trigger, formState } = form;
  const { restart, seconds, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {
      setTimerExpired(true);
      setIsTimerCompleted(true);
    }
  });
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const { isSubmitting } = formState;
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [isTimerCompleted, setIsTimerCompleted] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [loader, setLoader] = useState(false);
  const [otpValue, setOtpValue] = useState(Array(4).fill(""));
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [attemptNumber, setAttemptNumber] = useState(0);
  const history = useHistory();
  const [webEngage] = useWebEngageMutation();
  const { handleLeadCapture } = useLeadCapture();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.trackLoginPageViewed();
  }, []);

  const { setupDismissalListener, cleanup } = useRecaptchaDismissalDetector({
    onDismissed: () => {
      setLoader(false);
      setIsButtonDisabled(false);
      setError("phoneNumber", {
        type: "recaptcha",
        message: "Security verification cancelled. Please try again."
      });
    }
  });

  const onBlur = async (fieldName) => {
    if (fieldName === "phoneNumber") {
      if (phoneNumber && phoneNumber.length > 0) {
        if (phoneNumber.length !== selectedCountry.phoneLength) {
          setError("phoneNumber", {
            message: `Phone number must be ${selectedCountry.phoneLength} digits`
          });
        } else {
          clearErrors("phoneNumber");
        }
      } else if (!phoneNumber || phoneNumber.length === 0) {
        setError("phoneNumber", {
          message: `Phone number is required`
        });
      }
    } else {
      await trigger(fieldName);
    }
  };

  const handleCreateAcc = () => {
    history.push(ROUTES.STUDENT_CREATE_ACCOUNT);
  };

  const handlePhoneNumberChange = async (phoneNumber, country) => {
    setSelectedCountry(country);
    setValue("phoneNumber", phoneNumber);

    if (phoneNumber.length > 0 && phoneNumber.length < country.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${country.phoneLength} digits`
      });
      setIsButtonDisabled(true);
      return;
    }

    if (phoneNumber.length > country.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${country.phoneLength} digits`
      });
      setIsButtonDisabled(true);
      return;
    }

    if (phoneNumber.length === country.phoneLength) {
      clearErrors("phoneNumber");
      setIsButtonDisabled(false);

      const result = await verifyPhoneNumber({
        phoneNumber: phoneNumber,
        isdCode: country.isdCode
      });

      if (result?.data?.isVerified) {
        setError("phoneNumber", { message: "Phone number verification failed" });
        setIsButtonDisabled(false);
      } else {
        clearErrors("phoneNumber");
        setValue("phoneNumberVerified", true);
        setIsButtonDisabled(false);
      }
    }

    if (phoneNumber.length === 0) {
      clearErrors("phoneNumber");
      setIsButtonDisabled(true);
    }
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setValue("phoneNumber", "");
    clearErrors("phoneNumber");
    setIsButtonDisabled(true);

    if (isOtpSent) {
      setIsOtpSent(false);
      setShowOtpInput(false);
      setOtpValue(Array(4).fill(""));
      clearErrors("otpCode");
      setIsEditEnabled(false);
      restart();
      setIsTimerCompleted(true);
    }
  };

  const handleOtpCodeChange = (e) => {
    if (e.length >= 0 && e.length <= 3) {
      setError("otpCode", { type: "custom", message: "OTP must be exactly 4 digits." });
    } else {
      setError("otpCode", { type: "custom", message: "" });
    }
    const regex = /^[0-9]*$/;
    if (!regex.test(e)) {
      const otp = e.replace(/\D/g, "");
      setOtpValue([...otp]);
    } else {
      setOtpValue([...e]);
    }
    setIsButtonDisabled(e.length !== 4);
  };

  const clickSendOTP = async () => {
    analytics.trackLoginAttempted("otp");

    setOtpValue(Array(4).fill(""));
    setError("otpCode", { type: "custom", message: "" });
    setIsButtonDisabled(true);
    setLoader(true);

    if (!phoneNumber || phoneNumber.length !== selectedCountry.phoneLength) {
      analytics.trackLoginFailed("otp", "INVALID_PHONE_NUMBER", attemptNumber + 1);
      setError("phoneNumber", {
        message: `Phone number must be ${selectedCountry.phoneLength} digits`
      });
      setIsButtonDisabled(false);
      setLoader(false);
      return;
    }

    clearErrors("phoneNumber");
    setIsTimerCompleted(false);

    const result = await verifyPhoneNumber({
      phoneNumber: phoneNumber,
      isdCode: selectedCountry.isdCode
    });

    if (!result?.data?.detail) {
      analytics.trackLoginFailed("otp", "USER_NOT_FOUND", attemptNumber + 1);
      setShowOtpInput(false);
      setError("phoneNumber", { message: "Seems like you are a new user! Please create your account" });
      setIsButtonDisabled(true);
      setLoader(false);
      return;
    } else {
      setShowOtpInput(true);
    }

    const phoneNumberIsValid = phoneNumber?.length === selectedCountry.phoneLength;

    if (phoneNumberIsValid && result?.data?.detail) {
      setupDismissalListener();
      try {
        const otpResult = await sendOTP({
          phoneNumber: phoneNumber,
          isdCode: selectedCountry.isdCode
        });

        if (otpResult?.data) {
          const time = new Date();
          time.setSeconds(time.getSeconds() + 30);
          restart(time);
          setIsEditEnabled(true);
          setIsOtpSent(true);
          setLoader(false);
        } else {
          const errorData = otpResult?.error?.data || otpResult?.error || otpResult;
          const errorMessage = errorData?.message || errorData?.detail || "Failed to send OTP";
          const errorDetail = errorData?.detail || "";
          const errorReason = errorDetail.includes("reCAPTCHA") ? "RECAPTCHA_FAILED" : "OTP_SEND_FAILED";

          analytics.trackLoginFailed("otp", errorReason, attemptNumber + 1);

          if (errorDetail.includes("reCAPTCHA")) {
            setError("phoneNumber", {
              type: "recaptcha",
              message: "Security verification failed. Please try again."
            });
          } else {
            setError("phoneNumber", {
              type: "api",
              message: errorMessage
            });
          }
          setLoader(false);
          setIsButtonDisabled(false);
        }
      } catch (error) {
        analytics.trackLoginFailed("otp", "NETWORK_ERROR", attemptNumber + 1);
        setLoader(false);
        setIsButtonDisabled(false);
        const errorData = error?.error?.data || error?.error || error;
        const errorMessage =
          errorData?.message || errorData?.detail || "An unexpected error occurred. Please try again.";

        setError("phoneNumber", {
          type: "api",
          message: errorMessage
        });
      }
    } else {
      analytics.trackLoginFailed("otp", "PHONE_VALIDATION_FAILED", attemptNumber + 1);
      setLoader(false);
      setIsButtonDisabled(false);
    }
  };

  const clickVerifyOTP = async () => {
    const otpString = otpValue.join("");
    setIsTimerCompleted(true);

    await trigger("otpCode");

    if (otpString.length === 4) {
      setValue("otpCode", otpString);

      const result = await onStudentLoginOTP({
        otpCode: otpString,
        phoneNumber: phoneNumber,
        isdCode: selectedCountry.isdCode
      });

      if (result?.data?.code === 200) {
        const userId = result?.data?.details?.user?.admission_id || result?.data?.details?.user?.id || phoneNumber;
        analytics.trackLoginSuccess(userId, "otp");
        webengageLogin(result?.data?.details?.data);

        try {
          const call = false;
          if (call) {
            await webEngage({
              crn_id: `${result?.data?.details?.user?.crn_id}`,
              event_name: "Sign_In_Inmobius",
              event_data: [
                { attribute: "Student Login ID", value: `${result?.data?.details?.user?.admission_id}` },
                { attribute: "Student Login Password", value: `` },
                { attribute: "Phone", value: `${result?.data?.details?.user?.phone}` },
                { attribute: "Platform", value: "InMobius" },
                { attribute: "page_url", value: "/login-OTP" }
              ]
            });
          }
          await handleLeadCapture(result?.data?.details);
        } catch (error) {
          console.error("Error pushing WebEngage event:", error);
        }
      } else {
        const currentAttempt = attemptNumber + 1;
        setAttemptNumber(currentAttempt);

        if (result?.data?.code === 404 && result?.data?.details?.error_code === "USER_NOT_FOUND") {
          analytics.trackLoginFailed("otp", "USER_NOT_FOUND", currentAttempt);
          setError("phoneNumber", {
            type: "custom",
            message: result?.data?.details?.message || "Mobile number is not registered. Please register."
          });
          setIsButtonDisabled(true);
        } else if (result?.data?.code === 401 && result?.data?.details?.error_code === "INVALID_OTP") {
          analytics.trackLoginFailed("otp", "INVALID_OTP", currentAttempt);
          setError("otpCode", {
            type: "custom",
            message: result?.data?.details?.message || "OTP is incorrect or not valid"
          });
          setIsButtonDisabled(true);
        } else if (result?.data?.code === 400 && result?.data?.details?.error_code === "CREDENTIALS_AMBIGUOUS") {
          analytics.trackLoginFailed("otp", "CREDENTIALS_AMBIGUOUS", currentAttempt);
          setError("phoneNumber", {
            type: "custom",
            message: result?.data?.details?.message || "The provided credentials are associated with multiple accounts."
          });
          setIsButtonDisabled(true);
        } else if (result?.data?.code === 400 && result?.data?.details?.error_code === "MISSING_HEADERS") {
          analytics.trackLoginFailed("otp", "MISSING_HEADERS", currentAttempt);
          setError("otpCode", {
            type: "custom",
            message: result?.data?.details?.message || "Oops! Something went wrong. Please try again."
          });
          setIsButtonDisabled(true);
        } else if (result?.data?.code === 422 && result?.data?.detail) {
          analytics.trackLoginFailed("otp", "VALIDATION_ERROR", currentAttempt);
          const errorMessage = result?.data?.detail?.[0]?.msg || "Invalid input. Please check your credentials.";
          setError("phoneNumber", { type: "custom", message: errorMessage });
          setIsButtonDisabled(true);
        } else {
          analytics.trackLoginFailed("otp", "UNKNOWN_ERROR", currentAttempt);
          setError("otpCode", { type: "custom", message: "Oops! Something went wrong. Please try again." });
          setIsButtonDisabled(true);
        }
      }
    }
  };

  const handleEditNumberClick = () => {
    setIsEditEnabled(false);
    setShowOtpInput(false);
    setIsOtpSent(false);
    setValue("phoneNumber", "");
    setOtpValue(Array(4).fill(""));
    clearErrors("phoneNumber");
    Array(4)
      .fill("")
      .forEach((_, index) => {
        clearErrors(`otpCode[${index}]`);
      });
    restart();
    setIsTimerCompleted(true);
  };

  const handletoggle = () => {
    analytics.trackLoginMethodSelected("password");
    history.push(ROUTES.STUDENT);
  };

  const {
    formState: { errors },
    watch,
    setValue,
    clearErrors
  } = form;

  const [phoneNumber, ...otpCodes] = watch(["phoneNumber", ...Array(4).fill("otpCode")]);

  const disabled = useMemo(() => {
    return {
      phoneForm: isRunning || sendOTPLoading || isSubmitting,
      otpForm: !(isRunning || !isUninitialized) || isSubmitting,
      verifyButton: false
    };
  }, [isRunning, isUninitialized, sendOTPLoading, isSubmitting]);

  useEffect(() => {
    if (timerExpired && isOtpSent) {
      setTimerExpired(false);
    }
    if (timerExpired && !isOtpSent) {
      setShowOtpInput(false);
      setTimerExpired(false);
    }

    return cleanup;
  }, [timerExpired, isOtpSent, cleanup]);

  const handleButtonAction = async () => {
    if (!isButtonDisabled) {
      setIsButtonDisabled(true);
      if (isOtpSent) {
        setLoader(true);
        await clickVerifyOTP();
        setLoader(false);
      } else {
        await clickSendOTP();
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(clickVerifyOTP)}>
      <div className="row">
        <div className="col-12 position-relative mb-12">
          <label className="tab-lable">Phone Number</label>
          <PhoneInput
            form={form}
            disabled={isEditEnabled}
            handleChange={handlePhoneNumberChange}
            onBlur={() => onBlur("phoneNumber")}
            handleEditNumberClick={handleEditNumberClick}
            isEditEnabled={isEditEnabled}
            onCountryChange={handleCountryChange}
          />
          {errors.phoneNumber && <span className="error--strike--text">{errors.phoneNumber.message}</span>}
          {disabled.verifyButton &&
            otpCodes.map((_, index) => {
              return (
                errors[`otpCode[${index}]`] && (
                  <span className="error--strike--text" key={index}>
                    {errors[`otpCode[${index}]`].message}
                  </span>
                )
              );
            })}
        </div>
        {showOtpInput && (
          <div>
            {!disabled.verifyButton && (
              <div>
                <label className="tab-lable">Enter OTP</label>
                <div className="col-12 position-relative mb-12">
                  <div className="d-flex align-items-center">
                    <OtpInput
                      inputType="number"
                      skipDefaultStyles={false}
                      shouldAutoFocus="false"
                      value={otpValue.join("")}
                      onChange={(e) => handleOtpCodeChange(e)}
                      numInputs={4}
                      separator={<span> </span>}
                      isDisabled={disabled.otpForm}
                      inputStyle="otp-input-box"
                      renderInput={(props, index) => (
                        <input
                          {...props}
                          key={index}
                          type="text"
                          required
                          className={`otp-input-box ${errors.otpCode?.message ? "otp-error-strick" : "highlight"}`}
                        />
                      )}
                    />
                    <div className="resend_otp">
                      {!isTimerCompleted && (
                        <span>
                          Resend OTP <br />({seconds} Sec.)
                        </span>
                      )}
                      {isTimerCompleted && (
                        <button className="resend-btn" onClick={clickSendOTP}>
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                  {errors.otpCode && <span className="error--strike--text">{errors.otpCode.message}</span>}
                </div>
                <span className="t-c_class">
                  By logging in, you agree with our{" "}
                  <span onClick={toggleTAndC} className="gard--textflow mx-1">
                    T&C
                  </span>{" "}
                  and{" "}
                  <span className="gard--textflow mx-1" onClick={togglePAndP}>
                    PP
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <button className="button--membersubmit w-100" onClick={handleButtonAction} disabled={isButtonDisabled}>
        {isOtpSent ? (
          loader ? (
            <div className="loader-container">
              <img className="loader-gif" src={loaderGif} alt="Loading.." />
              <p className="loader-text">Loading</p>
            </div>
          ) : (
            "Continue"
          )
        ) : loader ? (
          <div className="loader-container">
            <img className="loader-gif" src={loaderGif} alt="Loading.." />
            <p className="loader-text">Loading</p>
          </div>
        ) : (
          "Send OTP"
        )}
      </button>

      <div className="hr-box">
        <hr className="hr-line" />
        <span>Or</span>
        <hr className="hr-line" />
      </div>
      <div className="col-12 mt-3 mb-3 pb-2 text-center">
        <a className="gard--textflow gard--textflow-mob" style={{ cursor: "pointer" }} onClick={handletoggle}>
          Log in with ID
        </a>
      </div>
      <div className="col-12 create-ac-link">
        <p className="login--supporttext">
          Don&apos;t have a Genius account?{" "}
          <span className="gard--textflow create-acoount" onClick={handleCreateAcc}>
            create account
          </span>
        </p>
      </div>
    </form>
  );
};
